import type { AppDisplay } from './client-type.js';

/**
 * **들어가면서 전체화면으로 갈 것인가** (2026-09-16 · 노트북 앱).
 *
 * ── 왜 제스처가 필요한가 ─────────────────────────────────────
 *
 * 웹은 **제스처 없이 전체화면에 들어갈 수 없다.** `requestFullscreen()` 은 사용자 활성화
 * 안에서만 성립하고, 밖에서 부르면 `TypeError` 로 거절된다. 그래서 「켜자마자」의 실제
 * 뜻은 **「이 문서의 첫 조작에서」**이고, 그 조작이 홈의 OK 이거나 채널 카드이거나 숫자
 * 키다(`use-entry-fullscreen.ts` 가 그 셋을 가리지 않는다).
 *
 * 매니페스트로는 안 된다 — `display: "fullscreen"` 은 **Android 전용**이고 데스크톱
 * Chrome 은 PWA 를 전체화면으로 띄우지 못한다(`docs/53` §2.9.6 · **캐시가 아니라 원리다**).
 * 실행기(`--app --start-fullscreen`)도 한 번 만들었다가 **버렸다** — 고칠 때마다 다시
 * 보내야 하는 배포본이라(같은 문서). 남는 길이 이것 하나다.
 *
 * ── 어디서 켜는가 ────────────────────────────────────────────
 *
 * **설치된 앱 창이고 손으로 쓰는 기기가 아닐 때** 하나다. `usesKeyboardNav` 와 같은
 * 재료를 읽지만 **같은 함수가 아니다** — 저쪽은 「화살표가 화면을 움직이는가」이고
 * 이것은 「창을 전체화면으로 만들 것인가」다. 지금 값이 겹치는 것은 우연이고,
 * **갈리는 날 한쪽만 고쳐야 한다**(`isRemoteControlled` 와 `promotesProgressOnLogin` 이
 * 같은 이유로 떨어져 있다).
 *
 * 갈래마다 이유가 있다 —
 *
 * - **브라우저 탭은 하지 않는다.** 탭을 열었을 뿐인 사람의 화면이 갑자기 전체화면이 되는
 *   것은 **요청하지 않은 변화**이고, v10·v19 가 탭을 건드리지 않은 그 기조다.
 * - **`fullscreen` 이 참이면 이미 전체화면이다**(⌃⌘F 로 띄운 창 · `AppDisplay` 의 표).
 *   `installed` 가 그때 거짓이라 이 판정도 저절로 거짓이고, 그것이 맞다 — 할 일이 없다.
 * - **손으로 쓰는 기기는 하지 않는다.** 안드로이드 홈 화면 앱은 이미 주소창이 없고,
 *   iOS 는 `<video>` 아닌 요소의 전체화면 자체를 지원하지 않는다(`webkitRequestFullscreen`
 *   이 문서에 없다) — 켜 봐야 거절이고, 거절을 조용히 삼키느니 묻지 않는 편이 낫다.
 */
export function wantsEntryFullscreen(display: AppDisplay): boolean {
  return display.installed && !display.handheld;
}
